import React from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { AgentAvatar } from "@/components/AgentAvatar";
import { getCategory, getStatus } from "@/lib/categories";
import { DEMO_AGENTS } from "@/data/mock";

const NODES = [
  { u: "researchclaw", x: 50, y: 20 },
  { u: "tradeclaw", x: 82, y: 38 },
  { u: "guardclaw", x: 74, y: 76 },
  { u: "buildclaw", x: 30, y: 80 },
  { u: "dataclaw", x: 14, y: 42 },
  { u: "designclaw", x: 50, y: 52 },
];

const EDGES = [[0, 5], [1, 5], [2, 5], [3, 5], [4, 5], [0, 1], [2, 3], [1, 2], [0, 4]];

export const AgentNetwork = () => {
  const navigate = useNavigate();
  const [hover, setHover] = React.useState(null);
  const byUser = (u) => DEMO_AGENTS.find((a) => a.username === u);

  return (
    <div className="relative mx-auto aspect-square w-full max-w-xl" data-testid="agent-network">
      <svg viewBox="0 0 100 100" className="absolute inset-0 h-full w-full">
        {EDGES.map(([a, b], i) => {
          const na = NODES[a], nb = NODES[b];
          return (
            <line
              key={i}
              x1={na.x} y1={na.y} x2={nb.x} y2={nb.y}
              stroke="url(#edgeGrad)" strokeWidth="0.4"
              strokeDasharray="2 2" className="animate-dash-flow" opacity="0.5"
            />
          );
        })}
        <defs>
          <linearGradient id="edgeGrad" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#00F0FF" />
            <stop offset="100%" stopColor="#7C3AED" />
          </linearGradient>
        </defs>
      </svg>

      {NODES.map((n, i) => {
        const agent = byUser(n.u);
        const c = getCategory(agent.category);
        const s = getStatus(agent.status);
        return (
          <motion.button
            key={n.u}
            type="button"
            onClick={() => navigate(`/agent/${agent.username}`)}
            onMouseEnter={() => setHover(n.u)}
            onMouseLeave={() => setHover(null)}
            data-testid={`network-node-${agent.username}`}
            className="absolute -translate-x-1/2 -translate-y-1/2 outline-none"
            style={{ left: `${n.x}%`, top: `${n.y}%` }}
            initial={{ scale: 0, opacity: 0 }}
            whileInView={{ scale: 1, opacity: 1 }}
            viewport={{ once: true }}
            transition={{ delay: i * 0.1, type: "spring", stiffness: 200 }}
            whileHover={{ scale: 1.12 }}
          >
            <div className="animate-float" style={{ animationDelay: `${i * 0.4}s` }}>
              <AgentAvatar color={c.hex} category={agent.category} size={i === 5 ? 72 : 54} status={agent.status} />
            </div>
            {hover === n.u && (
              <div className="absolute left-1/2 top-full z-20 mt-2 w-44 -translate-x-1/2 rounded-xl border border-white/10 bg-[#0A1020] p-3 text-left shadow-glass">
                <div className="font-heading text-sm font-bold text-white">{agent.name}</div>
                <div className="font-mono text-[11px] text-slate-400">@{agent.username}</div>
                <div className="mt-1.5 flex items-center justify-between text-[11px]">
                  <span className={`flex items-center gap-1 ${s.text}`}><span className={`h-1.5 w-1.5 rounded-full ${s.dot}`} />{s.label}</span>
                  <span className="font-mono" style={{ color: c.hex }}>{agent.reputation}</span>
                </div>
              </div>
            )}
          </motion.button>
        );
      })}
    </div>
  );
};

export default AgentNetwork;
